import React, { useState } from 'react';
import { registerWidget, getWidget } from '../widgetRegistry';

// Container widgets — used by the Designer to compose layouts. Built-in
// fallbacks are flat (single-widget trees), so containers see traffic only
// once admins start authoring custom layouts.

function ContainerStack({ config, children }) {
  const padding = config.padding === false ? 0 : '12px';
  const gap = typeof config.gap === 'number' ? `${config.gap}px` : '12px';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap, padding, flex: 1, minHeight: 0, overflow: 'auto' }}>
      {children}
    </div>
  );
}

registerWidget({
  type: 'container.stack',
  categories: ['home', 'case', 'task', 'add_case', 'add_task'],
  palette_label: 'Stack',
  description: 'Vertical stack — children render top-to-bottom with consistent spacing.',
  Component: ContainerStack,
  configSchema: {
    fields: [
      { type: 'boolean', name: 'padding', label: 'Padding', default: true },
      { type: 'number', name: 'gap', label: 'Gap (px)', default: 12 },
    ],
  },
  isContainer: true,
  group: 'Containers',
});


function ContainerColumns({ config, children }) {
  const cols = Math.max(1, Math.min(6, config.columns || 2));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '12px', padding: '12px', flex: 1, minHeight: 0, overflow: 'auto' }}>
      {children}
    </div>
  );
}

registerWidget({
  type: 'container.columns',
  categories: ['home', 'case', 'task', 'add_case', 'add_task'],
  palette_label: 'Columns',
  description: 'N-column grid; children flow into columns left-to-right.',
  Component: ContainerColumns,
  configSchema: {
    fields: [
      { type: 'number', name: 'columns', label: 'Columns', default: 2, min: 1, max: 6 },
    ],
  },
  isContainer: true,
  group: 'Containers',
});


function ContainerSplit({ config, children }) {
  const dir = config.direction === 'vertical' ? 'column' : 'row';
  return (
    <div style={{ display: 'flex', flexDirection: dir, flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {children}
    </div>
  );
}

registerWidget({
  type: 'container.split',
  categories: ['home', 'case', 'task', 'add_case', 'add_task'],
  palette_label: 'Split pane',
  description: 'Horizontal or vertical split. Children share available space.',
  Component: ContainerSplit,
  configSchema: {
    fields: [
      { type: 'select', name: 'direction', label: 'Direction', default: 'horizontal', options: [{ value: 'horizontal', label: 'Horizontal' }, { value: 'vertical', label: 'Vertical' }] },
    ],
  },
  isContainer: true,
  group: 'Containers',
});


function ContainerAccordion({ node, config, children }) {
  const sections = Array.isArray(node.children) ? node.children : [];
  const [openIdx, setOpenIdx] = useState(() => (config.default_open_index ?? 0));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '12px', flex: 1, minHeight: 0, overflow: 'auto' }}>
      {sections.map((s, i) => {
        const title = s.config?.title || getWidget(s.type)?.palette_label || `Section ${i + 1}`;
        const open = openIdx === i;
        const child = Array.isArray(children) ? children[i] : null;
        return (
          <div key={s.id || `acc-${i}`} style={{ border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)' }}>
            <button
              onClick={() => setOpenIdx(open ? -1 : i)}
              style={{ width: '100%', textAlign: 'left', background: 'transparent', color: 'var(--text-primary)', border: 'none', padding: '10px 12px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
            >
              <span>{title}</span><span>{open ? '▾' : '▸'}</span>
            </button>
            {open && <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border-color)' }}>{child}</div>}
          </div>
        );
      })}
    </div>
  );
}

registerWidget({
  type: 'container.accordion',
  categories: ['home', 'case', 'task', 'add_case', 'add_task'],
  palette_label: 'Accordion',
  description: 'Collapsible sections. Each child is a section; section title comes from the child config.',
  Component: ContainerAccordion,
  configSchema: {
    fields: [
      { type: 'number', name: 'default_open_index', label: 'Default open index', default: 0 },
    ],
  },
  isContainer: true,
  group: 'Containers',
});


function ContainerTabs({ node, config, children }) {
  const tabs = Array.isArray(node.children) ? node.children : [];
  const defaultId = config.default_tab || (tabs[0]?.id);
  const [active, setActive] = useState(defaultId);
  const activeIdx = Math.max(0, tabs.findIndex(t => t.id === active));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexShrink: 0 }}>
        {tabs.map((t, i) => {
          const label = t.config?.title || t.config?.tab_label || getWidget(t.type)?.palette_label || `Tab ${i + 1}`;
          const isActive = i === activeIdx;
          return (
            <button
              key={t.id || `tab-${i}`}
              onClick={() => setActive(t.id || `tab-${i}`)}
              style={{
                background: 'transparent',
                color: isActive ? 'var(--accent-color)' : 'var(--text-secondary)',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--accent-color)' : '2px solid transparent',
                padding: '10px 14px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >{label}</button>
          );
        })}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {Array.isArray(children) ? children[activeIdx] : null}
      </div>
    </div>
  );
}

registerWidget({
  type: 'container.tabs',
  categories: ['home', 'case', 'task', 'add_case', 'add_task'],
  palette_label: 'Tabs',
  description: 'Tabbed container. Each child is a tab; tab title comes from the child config.title.',
  Component: ContainerTabs,
  configSchema: {
    fields: [
      { type: 'text', name: 'default_tab', label: 'Default tab id', default: '' },
    ],
  },
  isContainer: true,
  group: 'Containers',
});
